import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { ADMIN_ROLES } from '@avanta/shared/constants/admins';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { UserType } from '@avanta/shared/constants/users';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { t } from '@avanta/shared/i18n';
import { type ColumnDef } from '@tanstack/react-table';
import { UserCog } from 'lucide-react';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PageHeader } from 'components/PageHeader';
import { useUserStore } from 'stores/user';
import { type AdminEnterprise } from 'types/enterprises';
import { AddAdministrator } from './components/AddAdministrator';
import { AdministratorActions } from './components/AdministratorActions';
import { useAdministratorsSummary } from './hooks/useAdministratorsSummary';
import { useFetchAdmins } from './hooks/useFetchAdmins';

const getColumns = (showActions: boolean): ColumnDef<AdminEnterprise>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: t('AdministratorsPage.Table.name.label', { ns: LOCIZE_NS.INSURANCE }),
    meta: {
      truncation: {
        maxWidthClassName: 'max-w-20',
      },
    },
    enableSorting: true,
  },
  {
    id: 'surname',
    accessorKey: 'surname',
    header: t('AdministratorsPage.Table.surname.label', { ns: LOCIZE_NS.INSURANCE }),
    meta: {
      truncation: {
        maxWidthClassName: 'max-w-20',
      },
    },
    enableSorting: true,
  },
  {
    id: 'contracts',
    accessorKey: 'contractIds',
    header: t('AdministratorsPage.Table.contracts.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): React.ReactElement => (
      <Text variant="text-xs-regular" className="text-denim-900">
        {row.original.contractIds?.length ?? 0}
      </Text>
    ),
  },
  {
    id: 'centers',
    accessorKey: 'centerIds',
    header: t('AdministratorsPage.Table.centers.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): React.ReactElement => (
      <Text variant="text-xs-regular" className="text-denim-900">
        {row.original.centerIds?.length ?? 0}
      </Text>
    ),
  },
  {
    id: 'role',
    accessorKey: 'role',
    header: t('AdministratorsPage.Table.role.label', { ns: LOCIZE_NS.INSURANCE }),
    meta: {
      columnFilter: {
        options: [
          {
            value: ADMIN_ROLES.ADMIN,
            label: t('AdministratorsPage.Table.role.options.ADMIN', { ns: LOCIZE_NS.INSURANCE }),
          },
          {
            value: ADMIN_ROLES.MANAGER,
            label: t('AdministratorsPage.Table.role.options.MANAGER', { ns: LOCIZE_NS.INSURANCE }),
          },
          {
            value: ADMIN_ROLES.BILLING,
            label: t('AdministratorsPage.Table.role.options.BILLING', { ns: LOCIZE_NS.INSURANCE }),
          },
        ],
      },
    },
    cell: ({ row }): React.ReactElement => (
      <Text tag="span" variant="text-sm-regular" className="text-denim-800">
        <Trans
          ns={LOCIZE_NS.INSURANCE}
          i18nKey={`AdministratorsPage.Table.role.value.${row.original.role}`}
          components={{
            description: <Text variant="text-xs-regular" className="text-gray-500" />,
          }}
        />
      </Text>
    ),
  },
  {
    id: 'email',
    accessorKey: 'email',
    header: (): string => t('AdministratorsPage.Table.email.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): React.ReactElement => (
      <Text variant="text-xs-regular" className="text-denim-900">
        {row.original.email}
      </Text>
    ),
  },
  ...(showActions
    ? [
        {
          id: 'actions',
          cell: ({ row }): React.ReactElement => <AdministratorActions admin={row.original} />,
        } satisfies ColumnDef<AdminEnterprise>,
      ]
    : []),
];

export const Administrators = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const userType = useUserStore(state => state.user?.type);
  const columns = useMemo(() => getColumns(userType === UserType.ENTERPRISE), [userType]);
  const { table } = useFetchAdmins(columns);
  const { stats: descriptionStats, isLoading: isDescriptionLoading } = useAdministratorsSummary();
  const { showDialog } = useDialogContext();

  if (!table) {
    return null;
  }

  const description = t('AdministratorsPage.description', descriptionStats);

  return (
    <div className="py-4">
      <PageHeader
        title={t('AdministratorsPage.heading')}
        description={description}
        isDescriptionLoading={isDescriptionLoading}
        className="mb-8"
      >
        <Button
          type="button"
          variant="secondary"
          size="md"
          icon="leading"
          className="-mt-4"
          onClick={() => showDialog({ component: <AddAdministrator /> })}
        >
          <UserCog />
          {t('InsuranceAdministrators.addAdmin.cta')}
        </Button>
      </PageHeader>
      <PaginatedTable<AdminEnterprise>
        table={table}
        columns={columns}
        emptyTitle={t('InsuredPersonsList.empty')}
        tableClassName="w-full bg-white [&_th:last-child]:w-28"
        searchPlaceholder={t('AdministratorsPage.searchPlaceholder')}
        withSearch
      />
    </div>
  );
};
